import React, { FormEvent, useState } from 'react';
import {
  TextInputMessage,
  TextInputMessageStatus,
  useModal,
} from '@pelando/components';
import { useTranslation } from '@/presentation/hooks/useTranslation';
import { At } from '@pelando/fontawesome/icons';
import { LockScrollGlobally } from '@/presentation/styles/global';
import {
  Button,
  EditNicknameModalStyle,
  IconTitle,
  ModalHeader,
  Subtitle,
  TextInput,
  Title,
} from './style';
import { ValidationStatus } from '../../../../../../domain/validation/Nickname/dtos';
import validateNickname from '../../../../../../domain/validation/Nickname';

type EditNicknameModalProps = {
  defaultValue?: string;
  updateNickname?: (
    value: string
  ) => Promise<{ success: boolean; error?: string }>;
};

export default function EditNicknameModal({
  defaultValue,
  updateNickname,
}: EditNicknameModalProps) {
  const [nickname, setNickname] = useState(defaultValue || '');
  const [isLoading, setLoading] = useState(false);
  const [validationMessage, setValidationMessage] = useState<
    TextInputMessage | undefined
  >(undefined);
  const { closeModal } = useModal();

  const { t } = useTranslation('common', 'profile');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const validation = validateNickname(value);

    setNickname(value);

    if (
      validation.type === ValidationStatus.ERROR_LENGTH ||
      validation.type === ValidationStatus.ERROR_PATTERN
    ) {
      return setValidationMessage({
        text: ValidationStatus.ERROR_LENGTH
          ? t('nickname_lenth_validation_message')
          : t('nickname_pattern_validation_message'),
        type: TextInputMessageStatus.ERROR,
      });
    }

    return setValidationMessage(undefined);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (defaultValue === nickname) {
      setLoading(false);
      setValidationMessage({
        text: t('validation-message-update-nickname'),
        type: TextInputMessageStatus.ERROR,
      });
      return;
    }

    if (validationMessage) {
      setLoading(false);
      return;
    }

    if (updateNickname) {
      updateNickname(nickname).then(({ success, error }) => {
        setLoading(false);
        if (success) {
          closeModal();
        }
        if (!success && error) {
          setValidationMessage({
            text: error,
            type: TextInputMessageStatus.ERROR,
          });
        }
      });
    }
  };

  const isInvalid =
    (validationMessage &&
      validationMessage.type === TextInputMessageStatus.ERROR) ||
    false;

  const title = t('nickname-modal-title');
  const subTitle = t('nickname-modal-subtitle');

  return (
    <>
      <ModalHeader>
        <IconTitle icon={At} />
        <Title>{title}</Title>
        <Subtitle>{subTitle}</Subtitle>
      </ModalHeader>
      <form onSubmit={handleSubmit}>
        <TextInput
          invalid={isInvalid}
          placeholder="ex: Emanuel123"
          defaultValue={nickname}
          onChange={handleChange}
          message={validationMessage}
          autoFocus
          prefix="@"
        />
        <Button type="submit" loading={isLoading}>
          {t('button-submit-edit-name')}
        </Button>
      </form>
    </>
  );
}

export function useEditNicknameModal({
  defaultValue,
  updateNickname,
}: EditNicknameModalProps) {
  const { showModal } = useModal();

  const showUpdateNicknameModal = () => {
    showModal(
      <>
        <EditNicknameModal
          defaultValue={defaultValue}
          updateNickname={updateNickname}
        />
        <LockScrollGlobally lock />
      </>,
      {
        style: EditNicknameModalStyle,
      }
    );
  };

  return { showUpdateNicknameModal };
}
